import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCheckout,
  createCheckout as createCheckoutService,
  fetchCheckoutSession,
  createAddress as createAddressService,
  updateAddress as updateAddressService,
  updateShippingAddress as updateShippingAddressService,
  updateBillingAddress as updateBillingAddressService,
  fetchInstallmentOptions as fetchInstallmentOptionsService,
} from "./checkout.service";
import {
  GetCheckoutResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  GetCheckoutSessionResponse,
  CreateAddressResponse,
  UpdateAddressResponse,
  UpdateShippingAddressRequest,
  UpdateShippingAddressResponse,
  UpdateBillingAddressRequest,
  UpdateBillingAddressResponse,
  GetInstallmentOptionsRequest,
  GetInstallmentOptionsResponse,
  GetCheckoutParams,
} from "./checkout.types";
import {
  CreateAddressRequest,
  UpdateAddressRequest,
} from "../address/address.types";
import { handleApiError } from "@/app/utils/handleApiError";

/**
 * Get checkout page data
 */
export const getCheckout = createAsyncThunk<
  GetCheckoutResponse,
  GetCheckoutParams | undefined,
  { rejectValue: string }
>("checkout/getCheckout", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchCheckout(params);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Create checkout session
 */
export const createCheckout = createAsyncThunk<
  CreateCheckoutResponse,
  CreateCheckoutRequest,
  { rejectValue: string | { requiresOtp: true; phone?: string } }
>("checkout/createCheckout", async (data, { rejectWithValue }) => {
  try {
    const response = await createCheckoutService(data);
    if (!response.status) {
      // Phone not verified — API returns data.items.sms_verified_at: false + phone_number
      const items = (response.data as any)?.items;
      if (items && items.sms_verified_at === false) {
        return rejectWithValue({ requiresOtp: true, phone: items.phone_number as string });
      }
      return rejectWithValue(response.message ?? "Checkout oluşturulamadı");
    }
    return response;
  } catch (error) {
    const data = (error as import("axios").AxiosError<any>)?.response?.data;
    const items = data?.data?.items;
    if (items && items.sms_verified_at === false) {
      return rejectWithValue({ requiresOtp: true, phone: items.phone_number as string });
    }
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Get checkout session by token
 */
export const getCheckoutSession = createAsyncThunk<
  GetCheckoutSessionResponse,
  string,
  { rejectValue: string }
>("checkout/getCheckoutSession", async (token, { rejectWithValue }) => {
  try {
    const response = await fetchCheckoutSession(token);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Create new address
 */
export const createAddress = createAsyncThunk<
  CreateAddressResponse,
  CreateAddressRequest,
  { rejectValue: string }
>("checkout/createAddress", async (data, { rejectWithValue }) => {
  try {
    const response = await createAddressService(data);
    if (!response.status) return rejectWithValue(response.message ?? "Adres eklenemedi");
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Update address
 */
export const updateAddress = createAsyncThunk<
  UpdateAddressResponse,
  { addressId: number; data: UpdateAddressRequest },
  { rejectValue: string }
>("checkout/updateAddress", async ({ addressId, data }, { rejectWithValue }) => {
  try {
    const response = await updateAddressService(addressId, data);
    if (!response.status) return rejectWithValue(response.message ?? "Adres güncellenemedi");
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Update shipping address for checkout session
 */
export const updateShippingAddress = createAsyncThunk<
  UpdateShippingAddressResponse,
  { token: string; data: UpdateShippingAddressRequest },
  { rejectValue: string }
>(
  "checkout/updateShippingAddress",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await updateShippingAddressService(token, data);
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Update billing address for checkout session
 */
export const updateBillingAddress = createAsyncThunk<
  UpdateBillingAddressResponse,
  { token: string; data: UpdateBillingAddressRequest },
  { rejectValue: string }
>(
  "checkout/updateBillingAddress",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await updateBillingAddressService(token, data);
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Fetch installment options by BIN number
 */
export const getInstallmentOptions = createAsyncThunk<
  GetInstallmentOptionsResponse,
  GetInstallmentOptionsRequest,
  { rejectValue: string }
>("checkout/getInstallmentOptions", async (data, { rejectWithValue }) => {
  try {
    const response = await fetchInstallmentOptionsService(data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});